import { useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  formatNumber,
  formatDuration,
  formatShortDate,
  type Gym,
  type User,
  type Workout,
  type Exercise,
} from '@jungles/shared';
import { api } from '../lib/api';
import { HistoryRow, type HistoryPage } from '../components/WorkoutHistory';
import { useApi } from '../lib/hooks';
import { useSession, useUser } from '../app/session';
import { useToast } from '../app/toast';
import {
  ErrorState,
  Header,
  JungleStrip,
  Loader,
  Screen,
  TabBar,
} from '../components/Chrome';
import { ChevronIcon, GymIcon } from '../components/icons';
import { GymPicker } from '../components/GymPicker';
import { GymLogo } from '../components/GymLogo';
import { WorkoutPicker } from '../components/WorkoutPicker';

/** Тренировки (SPEC §5). Домашний зал подставляется в каждую новую тренировку. */

interface WorkoutsSummary {
  today: string;
  last: Workout | null;
  thisWeek: Workout[];
  draft: Workout | null;
  chart: { date: string; volumeKg: number }[];
}

const GYM_HINT =
  'Подставляется в каждую новую тренировку — там же можно выбрать другой. Очки идут в рейтинг этого зала.';

export function workoutIcon(workout: Workout): string {
  if (workout.type === 'activity') return '/assets/ui/activity-cardio.webp';
  return workout.exercises[0]?.icon ?? '/assets/ui/tab-workouts.webp';
}

export function workoutSubtitle(workout: Workout): string {
  const parts = [formatShortDate(workout.date)];
  if (workout.durationMin) parts.push(formatDuration(workout.durationMin));
  if (workout.volumeKg > 0) parts.push(`${formatNumber(workout.volumeKg)} кг`);
  if (workout.type === 'activity' && workout.distanceKm) {
    parts.push(`${workout.distanceKm.toString().replace('.', ',')} км`);
  }
  return parts.join(' · ');
}

export function WorkoutsScreen() {
  const user = useUser();
  const { updateUser } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: exercises } = useApi<Exercise[]>('/api/exercises');

  const { data, error, loading, reload } = useApi<WorkoutsSummary>('/api/workouts/summary');
  const history = useApi<HistoryPage>('/api/workouts/history?limit=5');
  const location = useLocation();
  const [picker, setPicker] = useState(false);
  useLayoutEffect(() => {
    if ((location.state as {botWorkout?: boolean} | null)?.botWorkout) {
      setPicker(true);
      // Remove the one-shot trigger before history/reload can replay it.
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location, navigate]);
  const [gymPicker, setGymPicker] = useState(false);
  const [hint, setHint] = useState(false);

  const changeGym = async (gym: Gym | null) => {
    setGymPicker(false);
    if ((gym?.id ?? null) === (user.homeGymId ?? null)) return;
    try {
      const next = await api.patch<User>('/api/me', { homeGymId: gym?.id ?? null });
      updateUser(next);
      toast(gym ? `Зал сохранён: ${gym.name}` : 'Зал убран');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось сохранить зал');
    }
  };

  return (
    <Screen tabs>
      <Header />
      <h1>Тренировки</h1>

      <button className="new" type="button" onClick={() => setPicker(true)}>
        <span className="pl" aria-hidden="true">
          +
        </span>
        Новая тренировка
        <img src="/assets/ui/tab-workouts.webp" alt="" />
      </button>

      {loading ? <Loader /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}

      {data ? (
        <>
          {data.draft ? (
            <section className="card" style={{ marginTop: 16 }}>
              <h2>Незавершённый черновик</h2>
              <div>
                <b>{data.draft.title}</b>
                <div className="nt">{workoutSubtitle(data.draft)}</div>
              </div>
              <div className="acts">
                <button
                  className="b p"
                  type="button"
                  onClick={() => navigate(`/workouts/${data.draft!.id}`)}
                >
                  Продолжить
                </button>
              </div>
            </section>
          ) : null}

          <section className="card" style={{ marginTop: data.draft ? 0 : 16 }}>
            <div className="row">
              <h2 style={{ margin: 0 }}>Мой зал</h2>
              <button
                className="b ic"
                type="button"
                onClick={() => setHint((value) => !value)}
                aria-label="Зачем нужен зал"
              >
                ?
              </button>
            </div>
            <button
              className="gymf gbtn gym-catalog-card"
              type="button"
              style={{ marginTop: 12 }}
              onClick={() => setGymPicker(true)}
            >
              {user.homeGymName ? <GymLogo name={user.homeGymName} logoUrl={user.homeGymLogoUrl} size="medium" /> : <GymIcon />}
              <span className="gl">Постоянный</span>
              <span className="gval">
                {user.homeGymName ?? 'Без зала · дома или на улице'}
                {user.homeGymAddress ? <small className="gym-address">{user.homeGymAddress}</small> : null}
              </span>
              <ChevronIcon />
            </button>
            {hint ? (
              <p className="nt" style={{ margin: '10px 2px 0' }}>
                {GYM_HINT}
              </p>
            ) : null}
          </section>

          <section className="card history-preview">
            <h2>История тренировок</h2>
            {history.loading ? <Loader /> : null}
            {history.error ? <ErrorState message={history.error} onRetry={history.reload} /> : null}
            {!history.loading && !history.error && history.data?.items.length === 0 ? <p className="empty">Первая запись появится здесь после тренировки.</p> : null}
            <div className="wl">{history.data?.items.map(workout => <HistoryRow key={workout.id} workout={workout} exercises={exercises} timezone={user.timezone} compact />)}</div>
            <button className="b wide" type="button" style={{ marginTop: 14 }} onClick={() => navigate('/workouts/history')}>Все тренировки</button>
          </section>
        </>
      ) : null}

      <JungleStrip />
      <TabBar />
      {picker ? <WorkoutPicker onClose={() => setPicker(false)} /> : null}
      {gymPicker ? (
        <GymPicker
          title="Мой зал"
          currentId={user.homeGymId}
          onPick={(gym) => void changeGym(gym)}
          onClose={() => setGymPicker(false)}
        />
      ) : null}
    </Screen>
  );
}
